/**
 * JPBCaptureStage
 * ----------------
 * An off-viewport DOM "stage" used by the JPB report generation pipeline to
 * decouple image capture from the live UI.
 *
 * The problem it solves
 * ---------------------
 * The JPB DOCX report captures several images of live React components
 * (statistics card, polygon analyzer, histogram). If the user minimizes,
 * closes, or otherwise hides the JPB window mid-generation, the source
 * elements become detached / zero-sized and `html-to-image` silently
 * produces a blank PNG (or fails entirely).
 *
 * The fix is to clone the relevant DOM subtrees into a stage we control as
 * the very first step of report generation, then run all subsequent capture
 * work against the clones. Because the stage lives at `document.body` and the
 * app's stylesheets are global, the clones remain fully styled and
 * laid-out for the rest of the report generation regardless of what the
 * user does with the live UI.
 *
 * Positioning rationale
 * ---------------------
 * The stage is positioned at viewport (0, 0) and shifted off-screen with
 * `transform: translate(-200vw, 0)` instead of `left: -99999px`. Some
 * browsers / GPU drivers skip rasterizing content positioned far outside the
 * viewport coordinate space, which would defeat the purpose. A CSS
 * `transform` keeps the element in the layout viewport at (0, 0) while
 * visually shifting it off-screen, preserving rasterization for both 2D and
 * WebGL canvases that may be cloned into the stage.
 */

const STAGE_ID = "jpb-capture-stage";

export interface JPBCloneIntoOptions {
   /** Width to enforce on the cloned wrapper. Defaults to source.offsetWidth. */
   width?: number;
   /** Height to enforce on the cloned wrapper. Defaults to source.offsetHeight. */
   height?: number;
   /**
    * Open every `<details>` element inside the clone so collapsed sections
    * (e.g. "How this estimate is calculated") are captured. Does not touch
    * the live DOM.
    */
   expandDetails?: boolean;
   /**
    * If true (default), copy every class name from the source element's
    * ancestor chain onto the stage wrapper. This is required for any CSS
    * scoped through a descendant selector (e.g. native CSS nesting like
    * `.JPBModule { .jpb-stats-row { display: flex; } }`) to keep matching
    * the clone — without it, scoped rules silently stop applying and
    * flex/grid layouts collapse to block stacking in the snapshot.
    */
   mirrorAncestorClasses?: boolean;
   /** Extra class names to add to the wrapper, on top of the mirrored chain. */
   extraWrapperClassNames?: string[];
}

export class JPBCaptureStage {
   private readonly stage: HTMLDivElement;
   private readonly clones: HTMLElement[] = [];
   private destroyed = false;

   private constructor(stage: HTMLDivElement) {
      this.stage = stage;
   }

   /** Create a fresh capture stage and append it to `document.body`. */
   static create(): JPBCaptureStage {
      const existing = document.getElementById(STAGE_ID);
      if (existing && existing.parentNode) {
         existing.parentNode.removeChild(existing);
      }

      const stage = document.createElement("div");
      stage.id = STAGE_ID;
      // Off-viewport via transform (see file-level comment for rationale).
      stage.style.position = "fixed";
      stage.style.top = "0";
      stage.style.left = "0";
      stage.style.transform = "translate(-200vw, 0)";
      stage.style.pointerEvents = "none";
      stage.style.contain = "layout paint";
      stage.style.zIndex = "-1";
      // Critical: visibility must be `visible` (not `hidden`) — html-to-image
      // and the SVG rasterizer require the source nodes to be paintable.
      stage.style.visibility = "visible";
      stage.setAttribute("aria-hidden", "true");
      document.body.appendChild(stage);

      return new JPBCaptureStage(stage);
   }

   /**
    * Deep-clone a live element into the stage and return the attached clone.
    * The clone is wrapped in a sized container so its layout matches what the
    * user saw at the moment of capture, regardless of what happens to the
    * source element afterwards.
    */
   cloneInto(source: HTMLElement, options: JPBCloneIntoOptions = {}): HTMLElement {
      if (this.destroyed) {
         throw new Error("JPBCaptureStage.cloneInto called after destroy()");
      }

      const rect = source.getBoundingClientRect();
      const width = options.width ?? Math.ceil(rect.width || source.offsetWidth);
      const height = options.height ?? Math.ceil(rect.height || source.offsetHeight);

      const wrapper = document.createElement("div");
      wrapper.style.width = `${width}px`;
      wrapper.style.height = `${height}px`;
      wrapper.style.boxSizing = "border-box";
      wrapper.style.background = "#ffffff";
      wrapper.style.overflow = "visible";

      // Mirror ancestor classes so descendant-scoped CSS (e.g. CSS nesting
      // like `.JPBModule { .child { display: flex; } }`) keeps matching the
      // clone. Without this, scoped flex/grid layouts collapse to block.
      const mirrorAncestors = options.mirrorAncestorClasses ?? true;
      if (mirrorAncestors) {
         const ancestorClasses = this.collectAncestorClassNames(source);
         ancestorClasses.forEach((c) => wrapper.classList.add(c));
      }
      if (options.extraWrapperClassNames) {
         options.extraWrapperClassNames.forEach((c) => wrapper.classList.add(c));
      }

      const clone = source.cloneNode(true) as HTMLElement;
      // Strip identifiers from the clone so we never accidentally collide
      // with the live source via `document.getElementById`.
      this.stripIdsRecursively(clone);

      if (options.expandDetails) {
         // `<details open>` is a markup attribute; setting `open = true` on
         // the cloned nodes is enough — we don't touch the originals.
         clone.querySelectorAll("details").forEach((d) => {
            (d as HTMLDetailsElement).open = true;
         });
      }

      wrapper.appendChild(clone);
      this.stage.appendChild(wrapper);
      this.clones.push(wrapper);

      return clone;
   }

   /**
    * Wait until the stage is ready to be snapshotted: web fonts loaded plus
    * two animation frames so React / browser layout has settled.
    */
   async waitReady(): Promise<void> {
      try {
         if ((document as any).fonts?.ready) {
            await (document as any).fonts.ready;
         }
      } catch {
         // Non-fatal — fonts API not available or rejected.
      }
      await new Promise<void>((resolve) =>
         requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
   }

   /** Remove the stage and all clones from the DOM. Safe to call multiple times. */
   destroy(): void {
      if (this.destroyed) return;
      this.destroyed = true;
      this.clones.length = 0;
      if (this.stage.parentNode) {
         this.stage.parentNode.removeChild(this.stage);
      }
   }

   private stripIdsRecursively(root: HTMLElement): void {
      if (root.id) root.removeAttribute("id");
      const withIds = root.querySelectorAll<HTMLElement>("[id]");
      withIds.forEach((el) => el.removeAttribute("id"));
   }

   /**
    * Walks from `source.parentElement` up to (but not including) `<body>` and
    * returns the union of class names found along the way. We stop at `<body>`
    * because mirroring body-level globals (theme/language classes etc.) onto
    * a wrapper would risk matching unrelated rules.
    */
   private collectAncestorClassNames(source: HTMLElement): Set<string> {
      const classes = new Set<string>();
      let cursor: HTMLElement | null = source.parentElement;
      while (cursor && cursor !== document.body) {
         cursor.classList.forEach((c) => classes.add(c));
         cursor = cursor.parentElement;
      }
      return classes;
   }
}
